// The horizon: today as a band from when you got up to lights out, with now
// as the one warm point on it. The part of the day already gone is drawn in
// the sky's colour; what is left is a hairline. The evening (the recap time)
// is a small notch, so "how long until the evening" can be seen, not read.
import { useDates, useDays, useSettings, useTasks } from '@/db/hooks'
import { isTemplate } from '@/kinds/task/schema'
import { fixedOn } from '@/lib/dates'
import { useNow } from '@/hooks/useNow'
import { band, clockOf } from '@/lib/horizon'
import { todayLocal } from '@/lib/time'
import { cx } from './ui'

/**
 * Today's tasks with a time, as minutes of the day: the dots on the band. A
 * finished one stays, hollow, so the day's shape is still there to see.
 */
const useTimedToday = (): { id: string; minute: number; done: boolean; title: string }[] => {
  const today = todayLocal()
  return (useTasks() ?? [])
    .filter(
      (t) =>
        !t.deleted_at &&
        !isTemplate(t.spec) &&
        t.spec.scheduled === today &&
        /^\d{2}:\d{2}$/.test(t.spec.scheduled_time ?? '') &&
        t.status.state !== 'dropped',
    )
    .map((t) => ({
      id: t.id,
      minute: Number(t.spec.scheduled_time!.slice(0, 2)) * 60 + Number(t.spec.scheduled_time!.slice(3, 5)),
      done: t.status.state === 'done',
      title: t.spec.title,
    }))
}

export const Horizon = ({ className, labels = true }: { className?: string; labels?: boolean }) => {
  const now = useNow(60_000)
  const days = useDays()
  const settings = useSettings()
  const woke = days.find((d) => d.spec.date === todayLocal())?.spec.woke_at
  const b = band(now, woke, settings.recap_push)
  const pct = (x: number) => `${(x * 100).toFixed(2)}%`
  const along = (min: number) => Math.min(1, Math.max(0, (min - b.start) / (b.end - b.start)))
  // the day's appointments sit on the band, the way there as a thinner lead-in
  const fixed = fixedOn(useDates(), todayLocal()).filter((f) => f.end > b.start && f.start < b.end)
  const timed = useTimedToday().filter((t) => t.minute >= b.start && t.minute <= b.end)
  return (
    <div
      className={cx('horizon', className)}
      role="img"
      aria-label={`The day from ${clockOf(b.start)} to ${clockOf(b.end)}; now is ${clockOf(Math.round(b.start + b.now * (b.end - b.start)))}`}
      data-testid="horizon"
    >
      <div className="relative h-4">
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line-strong/70" />
        <div
          className="horizon-past absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full"
          style={{ width: pct(b.now) }}
        />
        {fixed.map((f) => (
          <span key={f.date.id} aria-hidden>
            {f.leave !== undefined && (
              <span
                className="absolute top-1/2 h-px -translate-y-1/2 bg-fg/50"
                style={{ left: pct(along(f.leave)), width: pct(along(f.start) - along(f.leave)) }}
              />
            )}
            <span
              className="horizon-fixed absolute top-1/2 h-[7px] -translate-y-1/2 rounded-full"
              style={{ left: pct(along(f.start)), width: `max(6px, ${pct(along(f.end) - along(f.start))})` }}
              data-testid="horizon-fixed"
            />
          </span>
        ))}
        {timed.map((t) => (
          // a task with a time today: a dot where it falls, hollow once done
          <span
            key={t.id}
            className={cx(
              'absolute top-1/2 h-[7px] w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full',
              t.done
                ? 'border border-fg/45 bg-[var(--color-ink)]'
                : 'bg-fg/85 shadow-[0_0_0_2px_var(--color-ink)]',
            )}
            style={{ left: pct(along(t.minute)) }}
            title={`${clockOf(t.minute)} ${t.title}`}
            data-testid="horizon-dot"
          />
        ))}
        {b.evening !== undefined && (
          <div
            className="absolute top-1/2 h-2.5 w-px -translate-y-1/2 bg-muted/70"
            style={{ left: pct(b.evening) }}
            aria-hidden
          />
        )}
        <div
          className="horizon-now absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ left: pct(b.now) }}
        />
      </div>
      {labels && (
        <div className="flex justify-between text-[11px] text-faint tabular -mt-0.5">
          <span>{clockOf(b.start)}</span>
          <span>{clockOf(b.end)}</span>
        </div>
      )}
    </div>
  )
}

/**
 * The horizon as an edge: a hairline along the top of the phone's tab bar with
 * now lit on it, so the day is in view from every page, not only Today and Home.
 */
export const HorizonEdge = ({ className }: { className?: string }) => {
  const now = useNow(60_000)
  const days = useDays()
  const settings = useSettings()
  const woke = days.find((d) => d.spec.date === todayLocal())?.spec.woke_at
  const b = band(now, woke, settings.recap_push)
  const timed = useTimedToday().filter((t) => t.minute >= b.start && t.minute <= b.end && !t.done)
  const along = (min: number) => Math.min(1, Math.max(0, (min - b.start) / (b.end - b.start)))
  return (
    <div className={cx('pointer-events-none', className)} aria-hidden data-testid="horizon-edge">
      {timed.map((t) => (
        <span
          key={t.id}
          className="absolute top-0 h-[4px] w-[4px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-fg/70"
          style={{ left: `${along(t.minute) * 100}%` }}
        />
      ))}
      <div
        className="horizon-past absolute left-0 top-0 h-[2px] rounded-full opacity-90"
        style={{ width: `${b.now * 100}%` }}
      />
      <div
        className="horizon-now horizon-now-sm absolute top-0 -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${b.now * 100}%` }}
      />
    </div>
  )
}
